import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, gql, useQuery } from "@apollo/client";
import { Select, SelectItem, TextInput, Modal } from "@carbon/react";
import TeacherSelector from "../../../Components/teacherSelector";
import UserCard from "../../../Components/userCard";

// --- GraphQL Definitions ---
const GET_SCHOOL_CLASS_BY_ID = gql`
    query SchoolClassById($id: String!) {
        schoolClassById(id: $id) {
            id
            name
            senior
            teacher {
                id
            }
            students {
                id
                username
                surname
                lastname
            }
        }
    }
`;

const GET_ALL_SCHOOL_CLASSES = gql`
    query GetAllSchoolClasses {
        allSchoolClasses {
            id
            name
        }
    }
`;

const EDIT_SCHOOL_CLASS = gql`
    mutation EditSchoolClass($id: String!, $name: String, $senior: Boolean, $teacherId: String) {
        editSchoolClass(id: $id, name: $name, senior: $senior, teacherId: $teacherId) {
            id
            name
            senior
        }
    }
`;

const DELETE_SCHOOL_CLASS = gql`
    mutation DeleteSchoolClass($id: String!) {
        deleteSchoolClass(id: $id) {
            id
            name
        }
    }
`;

export function EditClass() {
    const navigate = useNavigate();
    const { uid } = useParams(); // Get class ID from URL
    
    // --- State Management ---
    const [className, setClassName] = useState('');
    const [classType, setClassType] = useState('senior');
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [continueEnabled, setContinueEnabled] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [originalClassName, setOriginalClassName] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    // --- Hooks ---
    const { data: classData, loading, error: errorLoading } = useQuery(GET_SCHOOL_CLASS_BY_ID, {
        variables: { id: uid },
        fetchPolicy: 'cache-and-network'
    });
    
    const { data: allClassesData } = useQuery(GET_ALL_SCHOOL_CLASSES);
    const allClasses = allClassesData?.allSchoolClasses.filter(cls => cls.id !== uid) || [];
    
    const [editSchoolClass] = useMutation(EDIT_SCHOOL_CLASS, {
        refetchQueries: [{ query: GET_ALL_SCHOOL_CLASSES }],
        awaitRefetchQueries: true
    });
    const [deleteSchoolClass, { loading: deletingClass }] = useMutation(DELETE_SCHOOL_CLASS, {
        refetchQueries: [{ query: GET_ALL_SCHOOL_CLASSES }],
        awaitRefetchQueries: true
    });

    // Set form data once class data is loaded
    useEffect(() => {
        if (classData && classData.schoolClassById) {
            const classInfo = classData.schoolClassById;
            setClassName(classInfo.name);
            setOriginalClassName(classInfo.name);
            setClassType(classInfo.senior ? 'senior' : 'junior');
            setSelectedTeacher(classInfo.teacher?.id || null);
        }
    }, [classData]);

    // --- Validation Effect ---
    useEffect(() => {
        let error = '';
        if (!className.trim()) {
            error = 'Klassenname ist erforderlich.';
        } else if (className.trim() !== originalClassName && allClasses.some(cls => cls.name === className.trim())) {
            error = 'Klassenname muss eindeutig sein.';
        } else if (!selectedTeacher) {
            error = 'Bitte wähle einen Lehrer aus.';
        }

        setErrorMessage(error);
        setContinueEnabled(!error);
    }, [className, selectedTeacher, allClasses, originalClassName]);
    
    // Use students from classData
    const studentsInClass = classData?.schoolClassById?.students || [];

    // --- Event Handlers ---
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!continueEnabled) return;

        try {
            const { data } = await editSchoolClass({
                variables: {
                    id: uid,
                    name: className.trim(),
                    senior: classType === "senior",
                    teacherId: selectedTeacher,
                },
            });

            if (data && data.editSchoolClass) {
                navigate("/admin/classes");
            }
        } catch (error) {
            setErrorMessage(`Fehler beim Aktualisieren: ${error.message}`);
        }
    };

    const handleDeleteClass = async () => {
        try {
            await deleteSchoolClass({ variables: { id: uid } });
            navigate('/admin/classes');
        } catch (err) {
            alert("Fehler beim Löschen der Klasse: " + err.message);
        }
    };

    if (loading) {
        return <div>Lädt Klassendaten...</div>;
    }

    if (errorLoading) {
        return (
            <div className="py-4">
                <div className="max-w-2xl mx-auto text-center">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <h2 className="text-xl font-semibold text-red-800 mb-2">Klasse nicht gefunden</h2>
                        <p className="text-red-700 mb-4">Die angeforderte Klasse existiert nicht oder konnte nicht geladen werden.</p>
                        <button onClick={() => navigate('/admin/classes')} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded">
                            Zurück zur Klassenverwaltung
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="py-4">
            <div className="max-w-2xl">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-gray-900">Klasse bearbeiten</h1>
                    <button
                        onClick={() => setIsDeleteModalOpen(true)}
                        className={`text-sm font-semibold bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded ${studentsInClass.length > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={studentsInClass.length > 0}
                        title={studentsInClass.length > 0 ? 'Klasse kann nicht gelöscht werden, solange noch Schüler zugeordnet sind.' : ''}
                    >
                        Klasse löschen
                    </button>
                </div>

                <Modal
                    open={isDeleteModalOpen}
                    danger
                    modalHeading="Klasse wirklich löschen?"
                    primaryButtonText="Löschen"
                    secondaryButtonText="Abbrechen"
                    onRequestClose={() => setIsDeleteModalOpen(false)}
                    onRequestSubmit={handleDeleteClass}
                    primaryButtonDisabled={deletingClass}
                >
                    <p>
                        Wollen Sie die Klasse <strong>{className}</strong> wirklich löschen? Dies kann nicht rückgängig gemacht werden.
                    </p>
                </Modal>

                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                    <TextInput
                        id="className"
                        labelText="Klassenname"
                        placeholder="z.B. 10A"
                        autoComplete="off"
                        value={className}
                        onChange={(e) => setClassName(e.target.value)}
                        required
                    />
                    
                    <Select
                        id="classType"
                        labelText="Klassentyp"
                        value={classType}
                        onChange={(e) => setClassType(e.target.value)}
                    >
                        <SelectItem value="senior" text="Oberstufe" />
                        <SelectItem value="junior" text="Unterstufe" />
                    </Select>
                    
                    <TeacherSelector
                        onTeacherChange={setSelectedTeacher}
                        selectedTeacher={selectedTeacher}
                    />

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4">
                        <p className="text-sm text-red-600 h-5">
                            {errorMessage}
                        </p>
                        <button
                            type="submit"
                            className={`mainBtn w-full sm:w-auto text-white px-6 py-2 transition-opacity duration-200 ${
                                continueEnabled ? 'hover:bg-blue-700' : 'opacity-50 cursor-not-allowed'
                            }`}
                            disabled={!continueEnabled}
                        >
                            Klasse aktualisieren
                        </button>
                    </div>
                </form>
                
            </div>
            <section className="mt-8">
            <h2 className="mt-8 text-xl font-semibold text-gray-800">Schüler in dieser Klasse</h2>
                <div className="mt-4">
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {Array.from({ length: 8 }).map((_, index) => (
                                <div key={index} className="bg-gray-200 animate-pulse rounded-lg h-24"></div>
                            ))}
                        </div>
                    ) : (
                        studentsInClass.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {studentsInClass.map((user) => (
                                    <UserCard key={user.id} user={user} />
                                ))}
                            </div>
                        ) : (
                            <div className="w-full text-center py-20 bg-slate-50 rounded-lg">
                                <h3 className="text-xl font-medium text-slate-800">Keine Schüler in dieser Klasse</h3>
                                <p className="mt-2 text-base text-slate-500">Es sind aktuell keine Schüler dieser Klasse zugeordnet.</p>
                            </div>
                        )
                    )}
                </div>
            </section>
        </div>
    );
}